<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import KanbanAPI from '../../../../api/kanban';
import FunnelAPI from '../../../../api/funnel';
import conversationAPI from '../../../../api/inbox/conversation';
import KanbanItemDetails from './KanbanItemDetails.vue';
import KanbanItemForm from './KanbanItemForm.vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  forceReload: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['back', 'itemUpdated', 'quick-action']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const KANBAN_DETAIL_TAB_IDS = [
  'notes',
  'checklist',
  'attachments',
  'activities',
  'scheduled-messages',
  'other-items',
];

const initialKanbanTabFromRoute = computed(() => {
  const tab = route.query.tab;
  if (typeof tab !== 'string' || !KANBAN_DETAIL_TAB_IDS.includes(tab)) {
    return null;
  }
  return tab;
});

const syncKanbanTabQuery = tab => {
  const nextQuery = { ...route.query };
  if (tab && tab !== 'notes') {
    nextQuery.tab = tab;
  } else {
    delete nextQuery.tab;
  }
  router.replace({ query: nextQuery });
};

/** Datas — mesmo critério que AgendaTab / KanbanItemForm */
const formatDateOnlyLocal = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toLocalISOString = (date, includeTime = true) => {
  if (!date) return null;
  try {
    let d = new Date(date);
    if (typeof date === 'string' && date.length === 10) {
      const parts = date.split('-');
      d = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    if (!includeTime) {
      return `${year}-${month}-${day}`;
    }
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return null;
  }
};

/** Igual KanbanItem.vue: `YYYY-MM-DD` → parse no meio-dia local para o badge não mostrar o dia anterior */
const parseDateForDisplay = raw => {
  if (raw == null || raw === '') return null;
  const dateStr = typeof raw === 'string' ? raw : String(raw);
  let adjusted = dateStr;
  if (dateStr.length === 10) {
    adjusted = `${dateStr}T12:00:00`;
  }
  const date = new Date(adjusted);
  return Number.isFinite(date.getTime()) ? date : null;
};

const item = ref(null);
const isLoading = ref(true);
const isEditing = ref(false);
const forceReloadCounter = ref(0);
const STORAGE_KEY = 'kanban_show_funnel_progress';
const showFunnelProgress = ref(localStorage.getItem(STORAGE_KEY) !== 'false');

watch(showFunnelProgress, (newValue) => {
  localStorage.setItem(STORAGE_KEY, newValue.toString());
});

// Modal de mover funil
const showMoveFunnelModal = ref(false);
const availableFunnels = ref([]);
const selectedFunnelId = ref(null);
const selectedStage = ref(null);
const isMovingItem = ref(false);

// Mesmo critério do KanbanItem.vue: item tem conversa vinculada
const hasLinkedConversation = computed(() =>
  !!(item.value?.conversation || item.value?.item_details?.conversation_id)
);

const showLinkConversationModal = ref(false);
const linkConversationList = ref([]);
const loadingLinkConversations = ref(false);
const linkConversationSearch = ref('');
const linkConversationPage = ref(1);
const linkConversationHasMore = ref(true);
const loadingMoreLinkConversations = ref(false);

const showRescheduleModal = ref(false);
const rescheduleDeadlineValue = ref('');
const rescheduleDatetimeValue = ref('');
const rescheduleUseSchedule = ref(false);
const savingReschedule = ref(false);

const filteredLinkConversations = computed(() => {
  if (!linkConversationSearch.value) return linkConversationList.value;
  const q = linkConversationSearch.value.toLowerCase();
  return linkConversationList.value.filter(c => {
    const text = `${c.display_id} ${c.title}`.toLowerCase();
    return text.includes(q);
  });
});

const openLinkConversationModal = async () => {
  linkConversationSearch.value = '';
  linkConversationList.value = [];
  linkConversationPage.value = 1;
  linkConversationHasMore.value = true;

  // Abrir modal imediatamente
  showLinkConversationModal.value = true;
  loadingLinkConversations.value = true;

  try {
    const response = await conversationAPI.get({ page: 1 });
    const payload = response.data?.data?.payload ?? [];
    const mapped = payload.map(c => ({
      id: c.id,
      display_id: c.display_id,
      title: c.meta?.sender?.name || c.meta?.sender?.email || t('KANBAN.ITEM_CONVERSATION.NO_CONTACT'),
    }));
    linkConversationList.value = mapped;
    linkConversationHasMore.value = payload.length >= 25;
    linkConversationPage.value = 1;
  } catch (e) {
    emitter.emit('newToastMessage', { message: t('KANBAN.ERROR_LOAD_CONVERSATIONS') || 'Erro ao carregar conversas', type: 'error' });
  } finally {
    loadingLinkConversations.value = false;
  }
};

const loadMoreConversations = async () => {
  if (!linkConversationHasMore.value || loadingMoreLinkConversations.value) return;
  loadingMoreLinkConversations.value = true;
  try {
    const nextPage = linkConversationPage.value + 1;
    const response = await conversationAPI.get({ page: nextPage });
    const payload = response.data?.data?.payload ?? [];
    const mapped = payload.map(c => ({
      id: c.id,
      display_id: c.display_id,
      title: c.meta?.sender?.name || c.meta?.sender?.email || t('KANBAN.ITEM_CONVERSATION.NO_CONTACT'),
    }));
    linkConversationList.value = [...linkConversationList.value, ...mapped];
    linkConversationHasMore.value = payload.length >= 25;
    linkConversationPage.value = nextPage;
  } catch (e) {
    // silencioso — apenas para de carregar
  } finally {
    loadingMoreLinkConversations.value = false;
  }
};

const handleLinkConversationScroll = ({ target }) => {
  const { scrollTop, scrollHeight, clientHeight } = target;
  if (scrollHeight - scrollTop - clientHeight < 60) {
    loadMoreConversations();
  }
};

const onSelectConversationToLink = async (conversationId) => {
  if (!item.value || !conversationId) return;
  try {
    const currentDetails = item.value.item_details || {};
    await KanbanAPI.updateItem(item.value.id, {
      item_details: { ...currentDetails, conversation_id: conversationId },
    });
    const { data } = await KanbanAPI.getItem(item.value.id);
    item.value = data;
    showLinkConversationModal.value = false;
    emit('itemUpdated', data);
    emitter.emit('newToastMessage', { message: t('KANBAN.FORM.NOTES.LINK_CONVERSATION_SUCCESS') || 'Conversa vinculada', type: 'success' });
  } catch (e) {
    emitter.emit('newToastMessage', { message: t('KANBAN.ERROR_LINK_CONVERSATION') || 'Erro ao vincular conversa', type: 'error' });
  }
};

const statusInfo = computed(() => {
  if (!item.value || !item.value.item_details?.status) {
    return null;
  }
  const status = item.value.item_details.status;
  if (status === 'won') {
    const closedOffer = item.value.item_details.closed_offer;
    let label = t('KANBAN.BULK_ACTIONS.ITEM_STATUS.WON');

    if (closedOffer) {
      const currency = closedOffer.currency || item.value.item_details.currency;
      const value = closedOffer.value;
      const symbol = currency?.symbol || 'R$';
      const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency?.code || 'BRL'
      }).format(value);

      label = `${label} - Oferta fechada: ${formattedValue}`;
    }

    return {
      label,
      class: 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300',
      icon: 'checkmark-circle',
    };
  }
  if (status === 'lost') {
    return {
      label: t('KANBAN.BULK_ACTIONS.ITEM_STATUS.LOST'),
      class: 'bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-300',
      icon: 'dismiss-circle',
    };
  }
  return null;
});

const priorityInfo = computed(() => {
  if (!item.value || !item.value.item_details?.priority) {
    return null;
  }
  const priority = item.value.item_details.priority.toLowerCase();
  if (priority === 'none') return null;

  const priorityMap = {
    urgent: {
      label: t('KANBAN.PRIORITY_LABELS.URGENT') || 'Urgente',
      class: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    },
    high: {
      label: t('KANBAN.PRIORITY_LABELS.HIGH'),
      class: 'bg-n-ruby-3 dark:bg-n-ruby-8 text-n-ruby-9 dark:text-n-ruby-1',
    },
    medium: {
      label: t('KANBAN.PRIORITY_LABELS.MEDIUM'),
      class: 'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    },
    low: {
      label: t('KANBAN.PRIORITY_LABELS.LOW'),
      class: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    },
  };

  return priorityMap[priority] || null;
});

// Computed para informações do status do item
const itemStatusInfo = computed(() => {
  const status = item.value?.item_details?.status?.toLowerCase();

  const statusMap = {
    won: {
      label: t('KANBAN.BULK_ACTIONS.ITEM_STATUS.WON'),
    },
    lost: {
      label: t('KANBAN.BULK_ACTIONS.ITEM_STATUS.LOST'),
    },
  };

  // Se não há status definido, retorna "Aberto"
  if (!status) {
    return {
      label: 'Aberto',
    };
  }

  return statusMap[status] || {
    label: 'Aberto',
  };
});

// Método para obter o estilo do badge de status do item (mesmo estilo do KanbanItem.vue)
const getStatusBadgeStyle = () => {
  // Sempre pega do item_details.status, se não tiver marca como 'open'
  const effectiveStatus = item.value?.item_details?.status || 'open';
  
  switch (effectiveStatus) {
    case 'won':
      return {
        backgroundColor: '#dcfce7',
        color: '#166534',
        border: '1px solid #bbf7d0'
      };
    case 'lost':
      return {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fecaca'
      };
    case 'open':
      return {
        backgroundColor: '#f1f5f9',
        color: '#334155',
        border: '1px solid #e2e8f0'
      };
    case 'pending':
      return {
        backgroundColor: '#fef9c3',
        color: '#b45309',
        border: '1px solid #fde68a'
      };
    case 'resolved':
      return {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        border: '1px solid #bfdbfe'
      };
    case 'snoozed':
      return {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        border: '1px solid #d1d5db'
      };
    default:
      return {
        backgroundColor: '#f1f5f9',
        color: '#334155',
        border: '1px solid #e2e8f0'
      };
  }
};

// Modal de status
const showStatusModal = ref(false);
const statusForm = ref({
  status: '',
  reason: '',
  selectedOffer: null,
  selectedWinReason: null,
  selectedLossReason: null,
});

// Computed para buscar funnels ativos
const funnels = computed(() =>
  store.getters['funnel/getFunnels'].filter(funnel => funnel.active)
);

// Computed para buscar o funil atual do item
const currentFunnel = computed(() => {
  const funnelId = item.value?.funnel_id;
  
  if (!funnelId) return null;
  
  return funnels.value.find(f => f.id == funnelId);
});

// Computed para motivos de ganho
const winReasons = computed(() => {
  return currentFunnel.value?.settings?.win_reasons || [];
});

// Computed para motivos de perda
const lossReasons = computed(() => {
  return currentFunnel.value?.settings?.loss_reasons || [];
});

// Computed para obter as offers disponíveis do item
const availableOffers = computed(() => {
  const offers = item.value?.item_details?.offers || [];
  return Array.isArray(offers) ? offers : [];
});

const handleStatusClick = async () => {
  // Buscar funnels antes de abrir o modal
  try {
    await store.dispatch('funnel/fetch');
  } catch (error) {
    console.error('Erro ao buscar funnels:', error);
  }

  // Inicializa o form com o status atual
  const currentStatus = item.value?.item_details?.status || 'open';
  
  statusForm.value = {
    status: currentStatus,
    reason: item.value?.item_details?.reason || '',
    selectedOffer: item.value?.item_details?.closed_offer || null,
    selectedWinReason: item.value?.item_details?.win_reason || null,
    selectedLossReason: item.value?.item_details?.loss_reason || null,
  };

  showStatusModal.value = true;
};

const handleStatusSave = async () => {
  try {
    // Preparar o campo reason baseado no status
    let reasonText = null;
    
    if (statusForm.value.status === 'won') {
      // Se selecionou "Outro", usar o texto do textarea
      if (statusForm.value.selectedWinReason === 'other') {
        reasonText = statusForm.value.reason || null;
      } else {
        // Usar o título do win_reason selecionado
        const selectedReason = winReasons.value.find(
          r => r.id === statusForm.value.selectedWinReason
        );
        reasonText = selectedReason?.title || null;
      }
    } else if (statusForm.value.status === 'lost') {
      // Se selecionou "Outro", usar o texto do textarea
      if (statusForm.value.selectedLossReason === 'other') {
        reasonText = statusForm.value.reason || null;
      } else {
        // Usar o título do loss_reason selecionado
        const selectedReason = lossReasons.value.find(
          r => r.id === statusForm.value.selectedLossReason
        );
        reasonText = selectedReason?.title || null;
      }
    }
    
    // Sempre fazer update completo do item_details para garantir que o status seja atualizado
    const updatedItem = {
      ...item.value,
      item_details: {
        ...item.value.item_details,
        status: statusForm.value.status,
        reason: reasonText,
        closed_offer: statusForm.value.status === 'won' ? statusForm.value.selectedOffer : null,
        win_reason: statusForm.value.status === 'won' && statusForm.value.selectedWinReason !== 'other' ? statusForm.value.selectedWinReason : null,
        loss_reason: statusForm.value.status === 'lost' && statusForm.value.selectedLossReason !== 'other' ? statusForm.value.selectedLossReason : null,
      },
    };

    const { data } = await KanbanAPI.update(item.value.id, updatedItem);
    if (data) {
      item.value = data;
      emitter.emit('kanbanItemUpdated', data);
      store.commit('kanban/UPDATE_ITEM_WITH_RESPONSE', data);
    }

    showStatusModal.value = false;
    emit('itemUpdated');

    // Recarregar os dados do item
    await fetchItemDetails();

    // Mostrar toast de sucesso
    emitter.emit('newToastMessage', {
      message: 'Status atualizado com sucesso',
      type: 'success',
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao atualizar status',
      type: 'error',
    });
  }
};

// Computed para custom_attributes da conversa
const conversationCustomAttributes = computed(() => {
  const conversation = item.value?.conversation;
  if (!conversation?.custom_attributes) return {};

  // Filtrar apenas atributos não vazios
  const filtered = {};
  Object.entries(conversation.custom_attributes).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      filtered[key] = value;
    }
  });

  return filtered;
});

// Computed para formatted updated_at
const formattedUpdatedAt = computed(() => {
  if (!item.value?.updated_at) return '';
  try {
    const date = new Date(item.value.updated_at);
    return date.toLocaleString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '';
  }
});

// Computed para formatted deadline_at (mesmo ajuste de fuso que KanbanItem.vue)
const formattedDeadlineAt = computed(() => {
  if (!item.value?.item_details?.deadline_at) return '';
  try {
    const date = parseDateForDisplay(item.value.item_details.deadline_at);
    if (!date) return '';
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return '';
  }
});

const hasSchedulingHeader = computed(
  () =>
    !!(item.value?.item_details?.deadline_at || item.value?.item_details?.scheduled_at)
);

const schedulingHeaderPrefix = computed(() => {
  const d = item.value?.item_details;
  if (!d) return '';
  if (d.scheduled_at) {
    return t('KANBAN.CONVERSATION_ACTIONS.SCHEDULED_LABEL').replace(/\s*:\s*$/, '');
  }
  return t('KANBAN.DEADLINE');
});

const schedulingHeaderDateText = computed(() => {
  const d = item.value?.item_details;
  if (!d) return '';
  if (d.scheduled_at) {
    try {
      const date = parseDateForDisplay(d.scheduled_at);
      if (!date) return '';
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  }
  return formattedDeadlineAt.value;
});

const openRescheduleModal = () => {
  const d = item.value?.item_details;
  if (!d) return;
  if (d.scheduled_at) {
    rescheduleUseSchedule.value = true;
    rescheduleDatetimeValue.value = toLocalISOString(d.scheduled_at, true) || '';
    rescheduleDeadlineValue.value = '';
  } else {
    rescheduleUseSchedule.value = false;
    rescheduleDeadlineValue.value = toLocalISOString(d.deadline_at, false) || '';
    rescheduleDatetimeValue.value = '';
  }
  showRescheduleModal.value = true;
};

const saveReschedule = async () => {
  const it = item.value;
  if (!it?.id) return;

  try {
    savingReschedule.value = true;
    const details = { ...it.item_details };

    if (rescheduleUseSchedule.value) {
      if (!rescheduleDatetimeValue.value) {
        savingReschedule.value = false;
        return;
      }
      const currentDate = details.scheduled_at;
      const merged = new Date(rescheduleDatetimeValue.value);
      if (Number.isNaN(merged.getTime())) {
        emitter.emit('newToastMessage', {
          message: t('KANBAN.RESCHEDULE_ERROR'),
          type: 'error',
        });
        return;
      }
      const toDate = merged.toISOString();
      const updatedItemDetails = {
        ...details,
        scheduling_type: 'schedule',
        scheduled_at: toDate,
        deadline_at: null,
        rescheduled: true,
        rescheduling_history: [
          ...(details.rescheduling_history || []),
          {
            from_date: currentDate,
            to_date: toDate,
            changed_at: new Date().toISOString(),
          },
        ],
      };
      const payload = {
        funnel_id: it.funnel_id,
        funnel_stage: it.funnel_stage,
        position: it.position,
        item_details: updatedItemDetails,
        custom_attributes: it.custom_attributes || {},
      };
      await KanbanAPI.updateItem(it.id, payload);
    } else {
      if (!rescheduleDeadlineValue.value) {
        savingReschedule.value = false;
        return;
      }
      const parts = rescheduleDeadlineValue.value.split('-').map(Number);
      const target = new Date(parts[0], parts[1] - 1, parts[2]);
      const dateOnly = formatDateOnlyLocal(target);
      const currentDate = details.deadline_at || details.scheduled_at;
      const updatedItemDetails = {
        ...details,
        scheduling_type: 'deadline',
        deadline_at: dateOnly,
        scheduled_at: null,
        rescheduled: true,
        rescheduling_history: [
          ...(details.rescheduling_history || []),
          {
            from_date: currentDate,
            to_date: dateOnly,
            changed_at: new Date().toISOString(),
          },
        ],
      };
      const payload = {
        funnel_id: it.funnel_id,
        funnel_stage: it.funnel_stage,
        position: it.position,
        item_details: updatedItemDetails,
        custom_attributes: it.custom_attributes || {},
      };
      await KanbanAPI.updateItem(it.id, payload);
    }

    const { data } = await KanbanAPI.getItem(it.id);
    item.value = data;
    showRescheduleModal.value = false;
    emit('itemUpdated', data);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.RESCHEDULE_SUCCESS'),
      type: 'success',
    });
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.RESCHEDULE_ERROR'),
      type: 'error',
    });
  } finally {
    savingReschedule.value = false;
  }
};

// Funções para modal de mover funil
const openMoveFunnelModal = async () => {
  try {
    // Buscar todos os funis disponíveis
    const { data } = await FunnelAPI.get();

    // Filtrar para não incluir o funil atual do item
    availableFunnels.value = data.filter(funnel => funnel.id !== item.value?.funnel_id);

    // Não pré-selecionar nenhum funil, deixar o usuário escolher
    selectedFunnelId.value = null;
    selectedStage.value = null;

    showMoveFunnelModal.value = true;

    // Mostrar toast informativo
    emitter.emit('newToastMessage', {
      message: 'Selecione o funil e etapa para mover o item',
      type: 'info',
    });
  } catch (error) {
    console.error('Erro ao carregar funis:', error);

    // Mostrar toast de erro se houver problema ao carregar funis
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar funis disponíveis',
      type: 'error',
    });
  }
};

const closeMoveFunnelModal = () => {
  showMoveFunnelModal.value = false;
  selectedFunnelId.value = null;
  selectedStage.value = null;
};

const moveItemToFunnel = async () => {
  if (!selectedFunnelId.value || !item.value) return;

  try {
    isMovingItem.value = true;

    // Mover o item para o novo funil/etapa
    await KanbanAPI.moveToFunnel(
      item.value.id,
      selectedFunnelId.value,
      selectedStage.value
    );

    // Atualizar o funil selecionado no store se necessário
    if (selectedFunnelId.value !== item.value.funnel_id) {
      const newFunnel = availableFunnels.value.find(f => f.id === selectedFunnelId.value);
      if (newFunnel) {
        await store.dispatch('funnel/setSelectedFunnel', newFunnel);
      }
    }

    // Recarregar os dados do item
    await fetchItemDetails();

    // Emitir evento de atualização
    emit('itemUpdated');

    // Fechar modal
    closeMoveFunnelModal();

    // Mostrar toast de sucesso
    emitter.emit('newToastMessage', {
      message: 'Item movido para o novo funil com sucesso',
      type: 'success',
    });
  } catch (error) {
    console.error('Erro ao mover item:', error);

    // Mostrar toast de erro
    emitter.emit('newToastMessage', {
      message: 'Erro ao mover item para o novo funil',
      type: 'error',
    });
  } finally {
    isMovingItem.value = false;
  }
};

// Computed para o funil selecionado no modal
const selectedFunnelForModal = computed(() => {
  return availableFunnels.value.find(f => f.id === selectedFunnelId.value);
});

// Mover fetchItemDetails para antes do watch
const fetchItemDetails = async () => {
  try {
    isLoading.value = true;
    const { data } = await KanbanAPI.getItem(props.itemId);

    // Atualizar com os novos dados
    item.value = data;

    // Verificar se o funil do item está definido como selecionado
    // Isso é importante quando o item é acessado diretamente pela URL
    if (item.value && item.value.funnel_id) {
      const currentSelectedFunnel = store.getters['funnel/getSelectedFunnel'];
      if (!currentSelectedFunnel || currentSelectedFunnel.id !== item.value.funnel_id) {
        // Garantir que os funis estão carregados primeiro
        await store.dispatch('funnel/fetch');
        
        // Buscar o funil pelos funis disponíveis no store
        const funnels = store.getters['funnel/getFunnels'];
        const itemFunnel = funnels.find(f => f.id === item.value.funnel_id);
        if (itemFunnel) {
          // Definir o funil do item como selecionado
          await store.dispatch('funnel/setSelectedFunnel', itemFunnel);
        }
      }
    }
  } catch (error) {
    // Handle error silently
  } finally {
    isLoading.value = false;
  }
};

// Watch após a definição da função
watch(
  [() => props.itemId, () => props.forceReload],
  () => {
    // Limpar estado atual antes de buscar novo item
    item.value = null;
    isEditing.value = false;
    fetchItemDetails();
  },
  { immediate: true }
);

const handleBack = () => {
  // Limpar estados
  item.value = null;
  isEditing.value = false;

  // Remover o parâmetro 'item' da URL
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.item;
  delete currentQuery.tab;
  router.replace({ query: currentQuery });

  emit('back');
};

const handleEdit = async () => {
  // Recarregar os dados do item para garantir que temos as notas mais recentes
  await fetchItemDetails();
  isEditing.value = true;
};

const handleClose = () => {
  isEditing.value = false;
};

const handleItemUpdated = async () => {
  // Recarregar os dados do item para refletir as mudanças (como novas notas)
  await fetchItemDetails();
  emit('itemUpdated');
  isEditing.value = false; // Voltar para modo de visualização após edição
};

const handleDetailsDeleted = () => {
  // Remover o parâmetro 'item' da URL quando o item for deletado
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.item;
  delete currentQuery.tab;
  router.replace({ query: currentQuery });

  emit('itemUpdated');
  emit('back');
};

const handleQuickAction = (actionId) => {
  // Emitir evento para o componente pai lidar com a ação
  emit('quick-action', actionId);
};

const selectedFunnel = computed(() => {
  // First try to get from store
  const storeFunnel = store.getters['funnel/getSelectedFunnel'];
  if (storeFunnel) return storeFunnel;
  
  // If no funnel in store but we have item data, try to find the funnel by item's funnel_id
  if (item.value?.funnel_id) {
    const funnels = store.getters['funnel/getFunnels'];
    return funnels.find(f => f.id === item.value.funnel_id) || null;
  }
  
  return null;
});

/** Cores hex inline (sem tokens Tailwind) — tema claro/escuro via classe `dark` no body */
const isDark = ref(false);
const darkModeObserver = ref(null);

const syncBodyDark = () => {
  isDark.value = typeof document !== 'undefined' && document.body.classList.contains('dark');
};

onMounted(() => {
  syncBodyDark();
  darkModeObserver.value = new MutationObserver(syncBodyDark);
  darkModeObserver.value.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => {
  darkModeObserver.value?.disconnect();
});

const headerHover = ref({
  back: false,
  link: false,
  status: false,
  move: false,
  edit: false,
});

const backButtonStyle = computed(() => {
  const d = isDark.value;
  const h = headerHover.value.back;
  if (d) {
    return {
      color: '#94a3b8',
      backgroundColor: h ? '#1e293b' : 'transparent',
    };
  }
  return {
    color: '#475569',
    backgroundColor: h ? '#e2e8f0' : 'transparent',
  };
});

const deadlinePillStyle = computed(() =>
  isDark.value
    ? {
        border: '1px solid #b45309',
        backgroundColor: '#451a03',
        color: '#fef3c7',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
      }
    : {
        border: '1px solid #fcd34d',
        backgroundColor: '#fffbeb',
        color: '#78350f',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      }
);

const deadlineIconStyle = computed(() => ({
  color: isDark.value ? '#fbbf24' : '#b45309',
}));

const linkConversationBtnStyle = computed(() => {
  const d = isDark.value;
  const h = headerHover.value.link;
  if (d) {
    return {
      border: '1px solid',
      borderColor: h ? '#64748b' : '#475569',
      backgroundColor: h ? '#334155' : '#1e293b',
      color: '#e2e8f0',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)',
    };
  }
  return {
    border: '1px solid',
    borderColor: h ? '#cbd5e1' : '#e2e8f0',
    backgroundColor: h ? '#f8fafc' : '#ffffff',
    color: '#334155',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  };
});

const editHeaderBtnStyle = computed(() => {
  const d = isDark.value;
  const h = headerHover.value.edit;
  if (d) {
    return {
      border: '1px solid',
      borderColor: h ? '#64748b' : '#475569',
      backgroundColor: h ? '#334155' : '#1e293b',
      color: '#e2e8f0',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)',
    };
  }
  return {
    border: '1px solid',
    borderColor: h ? '#cbd5e1' : '#e2e8f0',
    backgroundColor: h ? '#f8fafc' : '#ffffff',
    color: '#334155',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  };
});

const statusBtnStyle = computed(() => {
  const h = headerHover.value.status;
  return {
    border: `1px solid ${h ? '#4338ca' : '#4f46e5'}`,
    backgroundColor: h ? '#4338ca' : '#4f46e5',
    color: '#ffffff',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.12)',
  };
});

const moveFunnelBtnStyle = computed(() => {
  const h = headerHover.value.move;
  return {
    border: `1px solid ${h ? '#1d4ed8' : '#2563eb'}`,
    backgroundColor: h ? '#1d4ed8' : '#2563eb',
    color: '#ffffff',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.12)',
  };
});

const checkboxStyle = computed(() => ({
  border: 'none',
  outline: 'none',
  accentColor: '#1f93ff',
}));
</script>

<template>
  <div
    class="flex flex-col h-full w-full bg-white dark:bg-slate-900 overflow-hidden"
  >
    <!-- Header -->
    <div class="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f93ff]/40 focus-visible:ring-offset-2"
          :style="[backButtonStyle, { '--tw-ring-offset-color': isDark ? '#0f172a' : '#ffffff' }]"
          @mouseenter="headerHover.back = true"
          @mouseleave="headerHover.back = false"
          @click="handleBack"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <div class="flex items-center gap-3">
          <h2 class="text-base font-medium">
            {{
              isLoading
                ? 'Carregando...'
                : item?.item_details?.title || t('ITEM_DETAILS')
            }}
          </h2>
          <div v-if="!isLoading" class="flex items-center gap-3">
            <div class="flex items-center gap-1.5">
              <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {{ t('KANBAN.STATUS_LABEL') }}:
              </span>
              <span
                class="px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1"
                :style="getStatusBadgeStyle()"
              >
                {{ itemStatusInfo.label }}
              </span>
            </div>

            <div v-if="priorityInfo" class="flex items-center gap-1.5">
              <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {{ t('KANBAN.PRIORITY_LABEL') }}:
              </span>
              <span
                class="px-2.5 py-1 text-xs font-medium rounded-full"
                :class="priorityInfo.class"
              >
                {{ priorityInfo.label }}
              </span>
            </div>
          </div>
          <!-- Custom Attributes da Conversa -->
          <div
            v-if="!isLoading && Object.keys(conversationCustomAttributes).length > 0"
            class="flex items-center gap-2"
          >
            <span
              v-for="(value, key) in conversationCustomAttributes"
              :key="key"
              class="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
              :title="`${key}: ${value}`"
            >
              {{ key }}: {{ value }}
            </span>
          </div>
          <!-- Updated At -->
          <div
            v-if="!isLoading && formattedUpdatedAt"
            class="text-[10px] text-slate-500 dark:text-slate-400 ml-2 hidden md:block"
          >
            {{ t('KANBAN.UPDATED_AT') || 'Atualizado em' }}: {{ formattedUpdatedAt }}
          </div>
        </div>
      </div>

      <div v-if="!isEditing && !isLoading" class="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5">
        <!-- Prazo / agendamento -->
        <div
          v-if="hasSchedulingHeader"
          class="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-3 py-2"
          :style="deadlinePillStyle"
        >
          <fluent-icon
            icon="calendar-clock"
            size="14"
            class="shrink-0"
            :style="deadlineIconStyle"
          />
          <span class="min-w-0 truncate text-xs font-medium tracking-wide">
            {{ schedulingHeaderPrefix }}: {{ schedulingHeaderDateText }}
          </span>
          <button
            type="button"
            class="shrink-0 text-xs font-medium underline underline-offset-2 opacity-90 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d97706]/40 rounded"
            @click="openRescheduleModal"
          >
            {{ t('KANBAN.RESCHEDULE') }}
          </button>
        </div>

        <!-- Botão Vincular à conversa (só quando não tiver conversa vinculada) -->
        <button
          v-if="!hasLinkedConversation"
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f93ff]/35 focus-visible:ring-offset-2"
          :style="[linkConversationBtnStyle, { '--tw-ring-offset-color': isDark ? '#0f172a' : '#ffffff' }]"
          @mouseenter="headerHover.link = true"
          @mouseleave="headerHover.link = false"
          @click="openLinkConversationModal"
        >
          {{ t('KANBAN.FORM.NOTES.LINK_CONVERSATION') || 'Vincular à conversa' }}
        </button>

        <!-- Checkbox para exibir/ocultar progresso -->
        <label
          v-if="!isLoading"
          class="flex cursor-pointer select-none items-center gap-2"
        >
          <input
            v-model="showFunnelProgress"
            type="checkbox"
            class="h-4 w-4 cursor-pointer rounded transition-all focus:outline-none focus:ring-0"
            :style="checkboxStyle"
          />
          <span class="text-xs font-medium text-slate-600 dark:text-slate-400">
            {{ t('KANBAN.SHOW_PROGRESS') }}
          </span>
        </label>

        <!-- Botão de mudar status -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#818cf8]/55 focus-visible:ring-offset-2"
          :style="[statusBtnStyle, { '--tw-ring-offset-color': isDark ? '#0f172a' : '#ffffff' }]"
          @mouseenter="headerHover.status = true"
          @mouseleave="headerHover.status = false"
          @click="handleStatusClick"
        >
          <fluent-icon icon="tag" size="14" class="shrink-0 opacity-95" style="color: #ffffff" />
          <span>{{ t('KANBAN.CHANGE_STATUS') || 'Mudar Status' }}</span>
        </button>

        <!-- Botão de mover funil -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#60a5fa]/55 focus-visible:ring-offset-2"
          :style="[moveFunnelBtnStyle, { '--tw-ring-offset-color': isDark ? '#0f172a' : '#ffffff' }]"
          @mouseenter="headerHover.move = true"
          @mouseleave="headerHover.move = false"
          @click="openMoveFunnelModal"
        >
          <fluent-icon icon="arrow-right" size="14" class="shrink-0 opacity-95" style="color: #ffffff" />
          <span>{{ t('KANBAN.MOVE_TO_FUNNEL') || 'Mover Funil' }}</span>
        </button>

        <!-- Botão de editar -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f93ff]/35 focus-visible:ring-offset-2"
          :style="[editHeaderBtnStyle, { '--tw-ring-offset-color': isDark ? '#0f172a' : '#ffffff' }]"
          @mouseenter="headerHover.edit = true"
          @mouseleave="headerHover.edit = false"
          @click="handleEdit"
        >
          <fluent-icon
            icon="edit"
            size="14"
            class="shrink-0"
            :style="{ color: isDark ? '#94a3b8' : '#64748b' }"
          />
          <span>{{ t('KANBAN.FORM.EDIT_ITEM') }}</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex-1 flex justify-center items-center py-12">
      <span
        class="w-8 h-8 border-2 border-t-woot-500 border-r-woot-500 border-b-transparent border-l-transparent rounded-full animate-spin"
      />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 min-h-0 overflow-hidden">
      <!-- Modo de visualização -->
      <KanbanItemDetails
        v-if="!isEditing && item"
        :item-id="item.id"
        :force-reload="forceReloadCounter"
        :show-funnel-progress="showFunnelProgress"
        :initial-kanban-tab="initialKanbanTabFromRoute"
        @close="handleBack"
        @edit="handleEdit"
        @item-updated="handleItemUpdated"
        @deleted="handleDetailsDeleted"
        @kanban-tab-changed="syncKanbanTabQuery"
      />

      <!-- Modo de edição -->
      <KanbanItemForm
        v-if="isEditing && item && (selectedFunnel || item.funnel_id)"
        :funnel-id="selectedFunnel?.id || item.funnel_id"
        :stage="item.funnel_stage"
        :position="item.position"
        :initial-data="item"
        is-editing
        @saved="handleItemUpdated"
        @close="handleClose"
      />
    </div>

    <!-- Modal de mover funil -->
    <div
      v-if="showMoveFunnelModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click="closeMoveFunnelModal"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden"
        @click.stop
      >
        <!-- Header do modal -->
        <div class="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center w-8 h-8 rounded-lg bg-woot-50 dark:bg-woot-900/20"
            >
              <fluent-icon icon="arrow-right" size="18" class="text-woot-500" />
            </div>
            <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {{ t('KANBAN.MOVE_TO_FUNNEL') || 'Mover para Funil' }}
            </h3>
          </div>
          <button
            @click="closeMoveFunnelModal"
            class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
          >
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </div>

        <!-- Corpo do modal -->
        <div class="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <!-- Seleção de funil -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.FUNNEL') || 'Funil' }}
            </label>
            <select
              v-model="selectedFunnelId"
              class="w-full px-3 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            >
              <option value="">{{ t('KANBAN.SELECT_FUNNEL') || 'Selecionar funil...' }}</option>
              <option
                v-for="funnel in availableFunnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
          </div>

          <!-- Seleção de etapa -->
          <div v-if="selectedFunnelForModal" class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.STAGE') || 'Etapa' }}
            </label>
            <select
              v-model="selectedStage"
              class="w-full px-3 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            >
              <option value="">{{ t('KANBAN.SELECT_STAGE') || 'Selecionar etapa...' }}</option>
              <option
                v-for="[stageId, stage] in Object.entries(selectedFunnelForModal.stages)"
                :key="stageId"
                :value="stageId"
              >
                {{ stage.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Footer do modal -->
        <div class="flex justify-end space-x-2 pt-4 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <button
            type="button"
            @click="closeMoveFunnelModal"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 rounded-lg transition-colors"
          >
            {{ t('KANBAN.CANCEL') || 'Cancelar' }}
          </button>
          <button
            type="button"
            @click="moveItemToFunnel"
            :disabled="!selectedFunnelId || isMovingItem"
            class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 disabled:bg-woot-400 disabled:cursor-not-allowed rounded-lg flex items-center gap-2 transition-colors"
          >
            <span v-if="isMovingItem" class="loading-spinner"></span>
            <fluent-icon v-else icon="arrow-right" size="14" />
            {{ isMovingItem ? (t('KANBAN.MOVING') || 'Movendo...') : (t('KANBAN.MOVE') || 'Mover') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal reagendar prazo -->
    <Modal
      v-model:show="showRescheduleModal"
      :on-close="() => (showRescheduleModal = false)"
      :show-close-button="true"
      size="small"
    >
      <div class="p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 shadow-lg">
        <h3 class="text-lg font-medium mb-4 text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.RESCHEDULE_MODAL_TITLE') }}
        </h3>
        <div class="space-y-4">
          <div v-if="rescheduleUseSchedule" class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.FORM.SCHEDULING.DATETIME') }}
            </label>
            <input
              v-model="rescheduleDatetimeValue"
              type="datetime-local"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div v-else class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.FORM.SCHEDULING.DEADLINE_DATE') }}
            </label>
            <input
              v-model="rescheduleDeadlineValue"
              type="date"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="showRescheduleModal = false"
            >
              {{ t('KANBAN.CANCEL') }}
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="savingReschedule"
              @click="saveReschedule"
            >
              {{ t('KANBAN.SAVE') }}
            </Button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal Vincular à conversa -->
    <Modal
      v-model:show="showLinkConversationModal"
      :on-close="() => (showLinkConversationModal = false)"
      :show-close-button="true"
      size="small"
    >
      <div class="p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 shadow-lg">
        <h3 class="text-lg font-medium mb-4">{{ t('KANBAN.FORM.NOTES.LINK_CONVERSATION') || 'Vincular à conversa' }}</h3>

        <!-- Loading inicial -->
        <div v-if="loadingLinkConversations && linkConversationList.length === 0" class="flex justify-center py-6">
          <span class="w-8 h-8 border-2 border-t-woot-500 border-r-woot-500 border-b-transparent border-l-transparent rounded-full animate-spin" />
        </div>

        <template v-else>
          <input
            v-model="linkConversationSearch"
            type="text"
            class="w-full px-3 py-2 mb-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-500"
            :placeholder="t('KANBAN.SEARCH_CONVERSATION') || 'Buscar conversa...'"
          />
          <div
            class="max-h-64 overflow-y-auto space-y-1"
            @scroll="handleLinkConversationScroll"
          >
            <button
              v-for="conv in filteredLinkConversations"
              :key="conv.id"
              type="button"
              class="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              @click="onSelectConversationToLink(conv.id)"
            >
              #{{ conv.display_id }} – {{ conv.title }}
            </button>

            <!-- Spinner de loading more -->
            <div v-if="loadingMoreLinkConversations" class="flex justify-center py-3">
              <span class="w-5 h-5 border-2 border-t-woot-500 border-r-woot-500 border-b-transparent border-l-transparent rounded-full animate-spin" />
            </div>

            <div
              v-if="!loadingLinkConversations && filteredLinkConversations.length === 0"
              class="px-3 py-2 text-slate-400 text-sm"
            >
              {{ t('KANBAN.NO_CONVERSATIONS_FOUND') || 'Nenhuma conversa encontrada' }}
            </div>
          </div>
        </template>
      </div>
    </Modal>

    <!-- Modal de Status -->
    <Modal
      v-model:show="showStatusModal"
      :on-close="() => (showStatusModal = false)"
      :show-close-button="true"
      size="small"
    >
      <div
        class="p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 shadow-lg"
      >
        <h3 class="text-lg font-medium mb-4">Atualizar status</h3>
        <div class="space-y-4">
          <div class="flex gap-2">
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              :class="
                statusForm.status === 'open'
                  ? 'border-slate-500 bg-slate-50 dark:bg-slate-800 font-bold text-slate-700 dark:text-slate-100'
                  : 'border-slate-100 dark:border-slate-800'
              "
              @click="statusForm.status = 'open'"
            >
              <span class="truncate">Em aberto</span>
              <span
                v-if="statusForm.status === 'open'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-green-50 dark:hover:bg-green-900"
              :class="
                statusForm.status === 'won'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900 font-bold text-green-700 dark:text-green-100'
                  : 'border-slate-100 dark:border-slate-800'
              "
              @click="statusForm.status = 'won'"
            >
              <span class="truncate">Ganho</span>
              <span
                v-if="statusForm.status === 'won'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-red-50 dark:hover:bg-red-900"
              :class="
                statusForm.status === 'lost'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900 font-bold text-red-700 dark:text-red-100'
                  : 'border-slate-100 dark:border-slate-800'
              "
              @click="statusForm.status = 'lost'"
            >
              <span class="truncate">Perdido</span>
              <span
                v-if="statusForm.status === 'lost'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
          </div>
          <div v-if="statusForm.status === 'lost'" class="space-y-2">
            <div v-if="lossReasons.length > 0" class="space-y-2">
              <label class="text-sm font-medium">Motivo da perda</label>
              <select
                v-model="statusForm.selectedLossReason"
                class="w-full p-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              >
                <option :value="null">Selecione um motivo</option>
                <option
                  v-for="reason in lossReasons"
                  :key="reason.id"
                  :value="reason.id"
                >
                  {{ reason.title }}
                </option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div v-if="statusForm.selectedLossReason === 'other' || !lossReasons.length" class="space-y-2">
              <label class="text-sm font-medium">{{ lossReasons.length > 0 ? 'Descreva o motivo' : 'Observações adicionais' }}</label>
              <textarea
                v-model="statusForm.reason"
                class="w-full p-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                rows="3"
                :placeholder="lossReasons.length > 0 ? 'Descreva o motivo da perda' : 'Descreva detalhes adicionais (opcional)'"
              />
            </div>
          </div>
          <div v-if="statusForm.status === 'won'" class="space-y-2">
            <div v-if="winReasons.length > 0" class="space-y-2">
              <label class="text-sm font-medium">Motivo do ganho</label>
              <select
                v-model="statusForm.selectedWinReason"
                class="w-full p-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              >
                <option :value="null">Selecione um motivo</option>
                <option
                  v-for="reason in winReasons"
                  :key="reason.id"
                  :value="reason.id"
                >
                  {{ reason.title }}
                </option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div v-if="statusForm.selectedWinReason === 'other'" class="space-y-2">
              <label class="text-sm font-medium">Descreva o motivo</label>
              <textarea
                v-model="statusForm.reason"
                class="w-full p-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                rows="3"
                placeholder="Descreva o motivo do ganho"
              />
            </div>
          </div>
          <div v-if="statusForm.status === 'won' && availableOffers.length > 0" class="space-y-2">
            <label class="text-sm font-medium">Selecione a oferta de fechamento</label>
            <div class="space-y-2 max-h-40 overflow-y-auto">
              <button
                v-for="(offer, index) in availableOffers"
                :key="index"
                type="button"
                class="flex items-center w-full px-3 py-2 rounded-md transition-colors gap-3 border border-slate-100 dark:border-slate-800 text-xs font-normal hover:bg-slate-100 dark:hover:bg-slate-800"
                :class="[
                  statusForm.selectedOffer === offer
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30 font-bold text-green-700 dark:text-green-300'
                    : '',
                ]"
                @click="statusForm.selectedOffer = offer"
              >
                <div class="flex flex-col items-start flex-1 min-w-0">
                  <div class="flex items-center gap-2 w-full">
                    <span class="font-medium truncate">{{ offer.description || 'Oferta sem descrição' }}</span>
                    <span class="text-green-600 dark:text-green-400 font-semibold text-xs">
                      {{ new Intl.NumberFormat(offer.currency?.locale || 'pt-BR', { style: 'currency', currency: offer.currency?.code || 'BRL' }).format(offer.value || 0) }}
                    </span>
                  </div>
                </div>
                <span
                  v-if="statusForm.selectedOffer === offer"
                  class="text-green-500 text-xs font-semibold flex-shrink-0"
                  >Selecionada</span
                >
              </button>
              <div
                v-if="availableOffers.length === 0"
                class="px-3 py-2 text-slate-400 text-xs text-center"
              >
                Nenhuma oferta disponível
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="showStatusModal = false"
              >Cancelar</Button
            >
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="
                (statusForm.status === 'won' && availableOffers.length > 0 && !statusForm.selectedOffer)
              "
              @click="handleStatusSave"
              >Salvar</Button
            >
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
/* Estilos para animação de carregamento */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.loading-spinner {
  @apply w-4 h-4 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}
</style>
